'use client'

import { fetchAutocompleteResults } from '@/app/api/search'
import Button from '@/app/components/common/Button'
import Popup from '@/app/components/common/Popup'
import FormInput from '@/app/components/Input'
import SearchAutosuggest from '@/app/components/SearchAutosuggest'
import {
	AvailableInputTypes,
	COOKIE_NOTICE_STORAGE_KEY,
	DATE_FORMAT_QUERY,
	DEFAULT_ALLOCATION,
	DEFAULT_CURRENCY,
	defaultCheckInDate,
	defaultCheckOutDate,
	FORM_TYPE,
	HOTEL_FORM_INPUT,
	INIT_DATA_STORAGE_KEY,
	INPUT,
	LG_BREAKPOINT,
	MD_BREAKPOINT,
	PAGE_TYPE,
	REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY,
	ROOM_GUESTS_SEPARATOR,
	SM_BREAKPOINT,
	SM_HEIGHT_BREAKPOINT,
} from '@/app/constants'
import {
	Allocation,
	SearchItem,
	SearchItemLocationType,
	SearchItemType,
	Suggestion,
	UserData,
} from '@/app/types'
import {
	capitalize,
	checkDate,
	getDefaultDatesForLocationType,
	getFormData,
	getGuestsCount,
	getLocaleByCode,
	getPlaceDetails,
	getRedirectPartnerInfo,
	parseSearchParamsToLower,
	scrollToSelectedMonth,
	serialize,
	shouldApplyDefaultDates,
	smoothScrollTo,
	submitHiddenForm,
	validateDateRange,
	validateEmail,
} from '@/app/utils'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { addDays, addMonths, format } from 'date-fns'
import { camelCase, find, kebabCase, startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { FC, FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import {
	SuggestionSelectedEventData,
	SuggestionsFetchRequestedParams,
} from 'react-autosuggest'

import { SanityData } from './Header'

import { priceAlertSubscription } from '@/app/api/subscribe'
import BookingDatepicker from '@/app/components/BookingDatepicker'
import BookingPlaceholder from '@/app/components/BookingPlaceholder'
import Checkbox from '@/app/components/common/Checkbox'
import FullScreenDrawer from '@/app/components/common/FullScreenDrawer'
import EmailSignup from '@/app/components/EmailSignup'
import GuestsSelect from '@/app/components/GuestsSelect'
import { DisplayData } from '@/app/components/LocationDataHandler'
import SiteHeading from '@/app/components/SiteHeading'
import { Icons } from '@/app/components/ui/Icons'
import eventBus from '@/app/eventBus'
import {
	emitFormEmailSubmitGAEvent,
	emitFormEmailSubscribeBounceGAEvent,
	emitFormSearchGAEvent,
	emitFromDateGAEvent,
	emitGuestsClickGAEvent,
	emitGuestsSelectGAEvent,
	emitSearchWithoutLocationGAEvent,
	emitSignUpDealsGAEvent,
	emitToDateGAEvent,
} from '@/app/ga-event-helpers'
import { PS_EVENTS } from '@/app/ps-events'
import * as Sentry from '@sentry/nextjs'
import dynamic from 'next/dynamic'
import { RemoveScroll } from 'react-remove-scroll'

export interface Props {
	displayValue?: DisplayData
	defaultLocation?: SearchItem | null
	isMainPage?: boolean
	sanityData?: SanityData
	loading?: boolean
	isHotelPage?: boolean
	customHeading?: boolean
	isExpanded?: boolean
	onClose?: () => void
}

const Booking: FC<Props> = ({
	displayValue,
	defaultLocation,
	isMainPage,
	sanityData,
	loading,
	onClose,
	isHotelPage,
	isExpanded,
	customHeading = false,
}) => {
	const router = useRouter()
	const searchParams = useSearchParams()
	const urlParams = useParams()
	const [_, setFrontFormData] = useLocalStorage(
		REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY
	)
	const [userData, setUserData] = useLocalStorage<UserData>(
		INIT_DATA_STORAGE_KEY
	)
	const [_cookie, setShowCookieNotice] = useLocalStorage(
		COOKIE_NOTICE_STORAGE_KEY
	)

	const getValidDate = (paramName: string, defaultDate: Date) => {
		const dateParam = searchParams.get(paramName)
		if (dateParam && checkDate(dateParam)) {
			const [year, month, day] = dateParam.split('-').map(Number)
			return new Date(year, month - 1, day)
		}
		return null
	}

	const checkInDate = getValidDate('check-in-date', defaultCheckInDate)
	const checkOutDate = getValidDate('check-out-date', defaultCheckOutDate)

	const lowerCaseSearchParams = parseSearchParamsToLower(searchParams)
	const destCode = lowerCaseSearchParams?.destcode
	const preopenCalendar = lowerCaseSearchParams?.['cal']
	const shouldPreopenCalendar = preopenCalendar === '1'
	const roomsInputRef = useRef<HTMLDivElement>(null)
	const bookingRef = useRef<HTMLDivElement>(null)
	const redirectFormRef = useRef<HTMLFormElement>(null)
	const redirectFormBlankRef = useRef<HTMLFormElement>(null)
	const bookingInputsRef = useRef<HTMLDivElement>(null)
	const bookingInputDestRef = useRef<HTMLInputElement>(null)
	const bookingInputGuestsRef = useRef<HTMLInputElement>(null)
	const selectedItemRef = useRef<SearchItem | null>(null)
	const isDesktop = useMediaQuery(LG_BREAKPOINT)
	const isMd = useMediaQuery(MD_BREAKPOINT)
	const isMobile = useMediaQuery(SM_BREAKPOINT)
	const smHeight = useMediaQuery(SM_HEIGHT_BREAKPOINT)
	const [suggestionsResult, setSuggestionsResult] = useState<SearchItem[]>([])
	const [suggestions, setSuggestions] = useState<Suggestion[]>([])
	const [selectedSuggestion, setSelectedSuggestion] = useState<string>()
	const [selectedItem, setSelectedItem] = useState<SearchItem | null>(null)
	const [searchValue, setSearchValue] = useState<DisplayData>()
	const [openedInput, setOpenedInput] = useState<AvailableInputTypes>(null)
	const t = useTranslations('Form')
	const locale = getLocaleByCode(urlParams.lang as string)
	const [dateFrom, setDateFrom] = useState<Date | null>(checkInDate || null)
	const [dateTo, setDateTo] = useState<Date | null>(checkOutDate || null)
	const [roomsAndGuests, setRoomsAndGuests] = useState<Allocation[]>([
		DEFAULT_ALLOCATION,
	])
	const [roomsAndGuestsText, setRoomsAndGuestsText] = useState<string>(
		t('roomsAndGuests', { roomsCount: 1, guestsCount: 2 })
	)
	const [alertChecked, setIsAlertChecked] = useState(false)
	const [previousDateFrom, setPreviousDateFrom] = useState<Date | null>(null)
	const [previousDateTo, setPreviousDateTo] = useState<Date | null>(null)
	const [isDatepickerOpen, setIsDatepickerOpen] = useState(false)
	const [datesAreChanged, setDatesAreChanged] = useState(false)
	const [inputError, setInputError] = useState<string | null>(null)
	const [isCompareChecked, setIsCompareChecked] = useState(true)
	const [userEmail, setUserEmail] = useState('')
	const [emailError, setEmailError] = useState<string>('')
	const [showNewHeading, setShowNewHeading] = useState(false)
	const [isOpenEmailSignup, setIsOpenEmailSignup] = useState(false)
	const {
		backTabPartner,
		frontTabPartner,
		hasBackTabRedirect,
		hasFrontPartner,
		isPartnerBooking,
	} = getRedirectPartnerInfo(searchParams)
	const [hotelPageFormHash, setHotelPageFormHash] = useState('')
	// const isFeatureOn = useFeatureIsOn('google_place')
	// const featureOnRef = useRef(false)

	useEffect(() => {
		const unsubscribe = eventBus.subscribe(
			PS_EVENTS.INPUT_FOCUS,
			(inputType: AvailableInputTypes) => {
				setOpenedInput(inputType)

				if (inputType === INPUT.LOCATION && bookingInputDestRef.current) {
					setTimeout(() => bookingInputDestRef.current?.focus(), 0)
				} else if (inputType === INPUT.CALENDAR_FROM) {
					setTimeout(() => {
						openCalendar(INPUT.CALENDAR_FROM)
					}, 0)
				} else if (inputType === INPUT.GUESTS_SELECT && roomsInputRef.current) {
					setTimeout(() => roomsInputRef.current?.focus(), 0)
				}
			}
		)

		return () => {
			unsubscribe()
		}
	}, [])

	const checkHash = useCallback(() => {
		const hotelPageFormHash = `#${HOTEL_FORM_INPUT.HOTEL_FORM}`
		const hash = window.location.hash
		setHotelPageFormHash(hotelPageFormHash)
	}, [])

	// useEffect(() => {
	// 	if (isFeatureOn) {
	// 		featureOnRef.current = true
	// 	}
	// }, [isFeatureOn])

	useEffect(() => {
		checkHash()
		window.addEventListener('hashchange', checkHash)

		return () => {
			window.removeEventListener('hashchange', checkHash)
		}
	}, [urlParams])

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowNewHeading(true)
		}, 600)

		return () => clearTimeout(timer)
	}, [])

	useEffect(() => {
		if (displayValue) {
			setSearchValue(displayValue)
		}
	}, [displayValue])

	useEffect(() => {
		if (defaultLocation) {
			setSelectedItem(defaultLocation)

			if (!selectedItemRef.current) {
				selectedItemRef.current = defaultLocation
			}
		}
	}, [defaultLocation])

	useEffect(() => {
		const checkInParam = searchParams.get('check-in-date')
		const checkOutParam = searchParams.get('check-out-date')

		// Use dates from params if they're valid
		let hasValidDates = false
		let newCheckIn: Date | null = null
		let newCheckOut: Date | null = null

		if (checkInParam && checkDate(checkInParam)) {
			const [year, month, day] = checkInParam.split('-').map(Number)
			newCheckIn = new Date(year, month - 1, day)
			setDateFrom(newCheckIn)
			hasValidDates = true
		}

		if (checkOutParam && checkDate(checkOutParam)) {
			const [year, month, day] = checkOutParam.split('-').map(Number)
			newCheckOut = new Date(year, month - 1, day)
			setDateTo(newCheckOut)
			hasValidDates = true
		}

		// Set previous dates if we got valid dates from params
		if (hasValidDates && newCheckIn && newCheckOut) {
			setPreviousDateFrom(newCheckIn)
			setPreviousDateTo(newCheckOut)
		}

		// If dates are still null or should apply defaults dates
		if (
			shouldApplyDefaultDates(checkInParam, checkOutParam, isMainPage) ||
			(!dateFrom && !dateTo)
		) {
			const isHotel = selectedItem?.loctype === SearchItemLocationType.HOTEL
			const { checkIn, checkOut } = getDefaultDatesForLocationType(isHotel)

			setDateFrom(checkIn)
			setDateTo(checkOut)
			// Also set previous dates when initializing
			setPreviousDateFrom(checkIn)
			setPreviousDateTo(checkOut)
		}
	}, [searchParams, selectedItem?.loctype, isMainPage])

	const calendarOpenedRef = useRef(false)

	useEffect(() => {
		// Only attempt to open the calendar once when the component mounts
		if (shouldPreopenCalendar && !calendarOpenedRef.current) {
			calendarOpenedRef.current = true

			if (isMainPage) {
				openCalendar(INPUT.CALENDAR_FROM)
			} else {
				if (!isExpanded) {
					const inputType = isDesktop ? INPUT.CALENDAR_FROM : INPUT.MAIN_FORM
					eventBus.publish(PS_EVENTS.INPUT_FOCUS, inputType)
				} else if (isDesktop) {
					setTimeout(() => {
						openCalendar(INPUT.CALENDAR_FROM)
					}, 300)
				}
			}
		}
	}, [shouldPreopenCalendar, isMainPage, isExpanded])

	useEffect(() => {
		if (selectedItem) {
			let formData

			if (
				selectedItem.loctype === SearchItemLocationType.HOTEL ||
				(isHotelPage && hasBackTabRedirect && isPartnerBooking)
			) {
				const buid = searchParams.get('buid') || null

				formData = getFormData({
					...selectedItem,
					dateFrom,
					dateTo,
					roomsAndGuests,
					backTabPartner,
					buid,
					formType: FORM_TYPE.BACK,
					displayname: `${selectedItem.name}, ${selectedItem.cityonly}, ${selectedItem.country}`,
				})
			} else {
				const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')
				const localDestcode = destCode?.includes(':')
					? destCode.substring(destCode.indexOf(':') + 1)
					: destCode

				formData = getFormData({
					...selectedItem,
					kayakType: 'city',
					...(selectedItem.ctid && { ptid: `${selectedItem.ctid}` }),
					displayname: selectedItem.cityname,
					dateFrom,
					dateTo,
					roomsAndGuests,
					formType: FORM_TYPE.FRONT,
					locationname: selectedItem.name,
					...(hasCityDestcode && { ptid: localDestcode }),
				})
			}

			setFrontFormData(formData)
		}
	}, [selectedItem, dateFrom, dateTo, roomsAndGuests])

	useEffect(() => {
		if (searchParams.has('room')) {
			let roomParam: string[] = searchParams.getAll('room')

			if (!Array.isArray(roomParam)) {
				roomParam = [roomParam]
			}

			const rooms = roomParam.map((room: string) => {
				const [adults, children = ''] = room.split(ROOM_GUESTS_SEPARATOR)
				const childAges = children
					? children.split(',').map((age) => Number(age))
					: []

				return {
					adults: parseInt(adults) || 1, // Default to 1 adult if parsing fails
					childAge: childAges,
				}
			})
			handleRoomsAndGuestsChange(rooms)
		}
	}, [searchParams])

	const fetchSuggestions = useCallback(
		async (query: string) => {
			try {
				const result = await fetchAutocompleteResults(
					query,
					urlParams.lang as string
				)
				setSuggestionsResult(result)

				const suggestions = result.map((item) => ({
					id: item.id,
					title: item.displayname,
					type: item.loctype,
					image: item.destination_images?.image_webp,
					locationName: item.locationname,
					displayName: item.displayname,
					placeID: item.placeID,
					country: item.country,
					cityonly: item.cityonly,
				}))

				setSuggestions(suggestions)
			} catch (error) {
				console.error('Error fetching suggestions:', error)
				setSuggestions([])
			}
		},
		[urlParams.lang]
	)

	const onSuggestionsFetchRequested = (
		params: SuggestionsFetchRequestedParams
	) => {
		fetchSuggestions(params.value)
	}

	const onDateChange = (dates: Date[]) => {
		if (dates[0]?.getTime() === dates[1]?.getTime()) {
			dates[1] = addDays(dates[1], 1)
		}

		if (dates[0]) {
			setDateFrom(dates[0])
			setOpenedInput(INPUT.CALENDAR_TO)
			if (dates[0] && !dates[1]) {
				emitFromDateGAEvent()
			}
		}

		setDateTo(dates[1] ? dates[1] : null)

		if (dates[1]) {
			if (isDesktop) {
				handleClose()
				setIsDatepickerOpen(false)
				setDatesAreChanged(true)
			}
			emitToDateGAEvent()
		}
	}

	const openCalendar = (input: INPUT) => {
		setTimeout(() => {
			handleOpenInput(input)
		}, 100)

		setIsDatepickerOpen(true)
	}

	const handleCancelDateSelection = () => {
		setDateFrom(previousDateFrom)
		setDateTo(previousDateTo)
		setIsDatepickerOpen(false)
		handleClose()
	}

	const onSuggestionSelected = (
		_event: FormEvent<any>,
		value: SuggestionSelectedEventData<Suggestion>
	) => {
		setInputError(null)

		if (value.suggestion.id) {
			const selected =
				find(suggestionsResult, { id: value.suggestion.id }) || null
			setSelectedItem(selected)
		} else {
			getPlaceDetails(value.suggestion.placeID).then((place) => {
				const city =
					place.addressComponents?.find((component) =>
						component.types.includes('locality')
					)?.longText ||
					place.addressComponents?.find((component) =>
						component.types.includes('administrative_area_level_2')
					)?.longText
				const country = place.addressComponents?.find((component) =>
					component.types.includes('country')
				)?.longText
				const region =
					place.addressComponents?.find((component) =>
						component.types.includes('administrative_area_level_1')
					)?.longText || city

				const mappedPlace: SearchItem = {
					id: place.id,
					lat: place.location.lat as number,
					lng: place.location.lng as number,
					name: place.displayName as string,
					displayname: place.displayName as string,
					country: country || '',
					region: region || '',
					cityonly: city || '',
					loctype: SearchItemLocationType.LM,
					placeID: place.id,
					smartyDisplay: place.formattedAddress as string,
					locationname: place.formattedAddress as string,
					cc:
						place.addressComponents?.find((component) =>
							component.types.includes('country')
						)?.shortText || '',
					rc: region || '',
					cityname: `${city || ''}, ${country || ''}`,
					timezone: null, // setting a default one
					utc: format(new Date().setUTCHours(place.utc as number, 0), 'xxx'),
					airportname: '',
					ap: '',
					apicode: '',
					ctid: '',
					displayType: place.displayType as SearchItemType,
					indexId: '',
					isMetroOnly: false,
					kayakId: '',
					kayakType: 'city',
					ptid: '',
					searchFormPrimary: place.displayName as string,
					searchFormSecondary: place.displayName as string,
					secondary: city || '',
					shortdisplayname: '',
					subregion: '',
					entityKey: '',
					googlePlaceId: place.id,
					utcOffsetMinutes: place.utc as number,
				}

				setSelectedItem(mappedPlace)
				setSelectedSuggestion(mappedPlace.displayname)
			})
		}

		setSelectedSuggestion(value.suggestionValue)
		handleClose()

		if (!datesAreChanged) {
			setTimeout(() => {
				openCalendar(INPUT.CALENDAR_FROM)
			}, 200)
		}
	}

	const onInputChange = (value: string) => {
		setInputError(null)
		if (value !== selectedSuggestion) {
			// Removing the suggestion when
			setSelectedItem(null)
		}
	}

	const handleRoomsAndGuestsChange = (rooms: Allocation[]) => {
		const roomsCount = rooms.length
		const guestsCount = getGuestsCount(rooms)

		setRoomsAndGuests(rooms)
		setRoomsAndGuestsText(t('roomsAndGuests', { roomsCount, guestsCount }))

		// handleClose()
	}

	const handleFreeRegion = (selectedItem: SearchItem, dest: string) => {
		const { name, smartyDisplay } = selectedItem

		if (selectedItem.country) {
			return `/${kebabCase(selectedItem.country)}/${kebabCase(name)}/`
		}
		return `${dest}/${kebabCase(smartyDisplay)}/${kebabCase(
			smartyDisplay
		)}/freereg/`
	}

	const handleSearch = () => {
		// smoothScrollTo(0, 0)

		if (!selectedItem) {
			handleLocationError()
			return
		}

		const frontTabRedirectCriteria =
			!hasBackTabRedirect && isMainPage && hasFrontPartner

		const current = new URLSearchParams(Array.from(searchParams.entries()))
		const existingParams = parseSearchParamsToLower(
			Object.fromEntries(current.entries())
		)

		const allowedPartners = searchParams.get('ap') || null
		const buid = searchParams.get('buid') || null
		const currency =
			searchParams.get('currency') ||
			userData?.selectedCurrency?.abbreviation ||
			userData?.defaultCurrency?.abbreviation ||
			DEFAULT_CURRENCY
		const userCountry = userData?.userCountry

		const {
			country,
			region,
			loctype,
			cityonly,
			name,
			smartyDisplay,
			kayakId: hotelID,
			placeID,
			airportname,
			timezone,
			ctid,
			cc: countryCode,
			googlePlaceId,
			lat,
			lng,
			utcOffsetMinutes,
		} = selectedItem

		const buildDestination = () => {
			let basePath = urlParams.lang ? `/${urlParams.lang}` : ''

			if (isHotelPage && selectedItem.id === selectedItemRef.current?.id) {
				basePath += '/hotel'
			}

			switch (loctype) {
				case SearchItemLocationType.AIRPORT:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || ''
					)}/ap/${encodeURIComponent(airportname || name)}`
				case SearchItemLocationType.HOTEL:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/${encodeURIComponent(name)}`
				case SearchItemLocationType.FREEREGION:
					return handleFreeRegion(selectedItem, basePath)
				case SearchItemLocationType.CITY:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}`
				case SearchItemLocationType.LM:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/lm/${encodeURIComponent(name)}`
				// return `${basePath}/${kebabCase(country)}${
				// 	region ? `/${kebabCase(region)}` : ''
				// }${cityonly ? `/${kebabCase(cityonly)}` : ''}`
				case SearchItemLocationType.HOOD:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/hood/${encodeURIComponent(name)}`
				default:
					return `${basePath}/${kebabCase(country)}${
						region ? `/${kebabCase(region)}` : ''
					}`
			}
		}

		const dest = buildDestination()

		// Validate dates before formatting
		if (!validateDateRange(dateFrom, dateTo) || !dateFrom || !dateTo) {
			console.error('Date validation failed')
			Sentry.captureMessage('Date validation failed in handleSearch', {
				level: 'error',
				tags: {
					component: 'Booking Form',
					action: 'handleSearch',
					issue: 'date_validation_failed',
				},
				extra: {
					dateFrom: dateFrom,
					dateTo: dateTo || 'Not Selected',
					maxAllowedDate: addMonths(new Date(), 12),
					selectedItem: selectedItem
						? {
								id: selectedItem.id,
								name: selectedItem.name,
								loctype: selectedItem.loctype,
						  }
						: null,
					userAgent: navigator.userAgent,
				},
			})
			return
		}

		const formattedDateFrom = format(dateFrom, DATE_FORMAT_QUERY)
		const formattedDateTo = format(dateTo, DATE_FORMAT_QUERY)

		const rooms = roomsAndGuests.map(
			(room) =>
				`${room.adults}|${room.childAge.map((age) => age.toString()).join(',')}`
		)

		const {
			loctype: paramLoctype,
			entitykey: paramEntityKey,
			slug,
			destcode: paramDestCode,
			locationname,
			placeid: paramPlaceID,
			hotelid: paramHotelID,
			usercountry: paramUserCountry,
			sessionid: paramSessionID,
			timezone: paramTimezone,
			f: frontParam,
			latlon: paramLatLon,
			utcoffsetminutes: paramUtcOffsetMinutes,
			googleplaceid: paramGooglePlaceID,
			page,
			mx: maxPriceParam,
			mn: minPriceParam,
			cal: openCalendarParam,
			...restParams
		} = existingParams || {}

		// This is to remove filters from the results page
		const filteredParams = isMainPage
			? restParams
			: Object.fromEntries(
					Object.entries(restParams).filter(
						([key]) =>
							!['s', 'h', 't', 'gr', 'by', 'dir', 'p', 'f'].includes(key)
					)
			  )

		const urlParamsObject = new URLSearchParams()
		urlParamsObject.set('currency', currency)
		urlParamsObject.set('check-in-date', formattedDateFrom)
		urlParamsObject.set('check-out-date', formattedDateTo)
		urlParamsObject.set('userCountry', userCountry)

		const cityName = selectedItem.cityonly && selectedItem.cityonly

		if (selectedItem.loctype === SearchItemLocationType.AIRPORT) {
			urlParamsObject.set('locationName', cityName || selectedItem.name)
		} else if (selectedItem.loctype === SearchItemLocationType.CITY) {
			urlParamsObject.set('locationName', cityName)
		} else if (selectedItem.loctype === SearchItemLocationType.HOTEL) {
			urlParamsObject.set('locationName', selectedItem.name)
		} else if (
			selectedItem.loctype === SearchItemLocationType.REGION ||
			selectedItem.loctype === SearchItemLocationType.COUNTRY ||
			selectedItem.loctype === SearchItemLocationType.FREEREGION
		) {
			urlParamsObject.set(
				'locationName',
				startCase(camelCase(selectedItem.name))
			)
		}

		// if (customEntityKey) {
		// 	urlParamsObject.set('entityKey', customEntityKey)
		// }
		if (userData?.sessionID) {
			urlParamsObject.set('sessionID', userData?.sessionID)
		}
		if (timezone) {
			urlParamsObject.set('timezone', timezone)
		}
		if (ctid) {
			urlParamsObject.set('ctid', ctid)
		}
		if (countryCode) {
			urlParamsObject.set('cc', countryCode)
		}
		if (placeID) {
			urlParamsObject.set('placeID', placeID)
		}

		if (
			selectedItem.loctype === SearchItemLocationType.HOTEL &&
			selectedItem.kayakId
		) {
			urlParamsObject.set('hotelID', hotelID)
		}
		if (allowedPartners) {
			urlParamsObject.set('ap', allowedPartners)
		}
		if (googlePlaceId) {
			urlParamsObject.set('googlePlaceId', googlePlaceId)
			urlParamsObject.set('latlon', `${lat},${lng}`)
			urlParamsObject.set('utcOffsetMinutes', String(utcOffsetMinutes))
		}

		rooms.forEach((room) => {
			urlParamsObject.append('room', room)
		})

		Object.entries(filteredParams).forEach(([key, value]) => {
			if (!urlParamsObject.has(key)) {
				urlParamsObject.set(key, value)
			}
		})

		const url = `${dest}?${urlParamsObject.toString()}`

		setShowCookieNotice(false)

		emitFormSearchGAEvent({
			dateFrom: formattedDateFrom,
			dateTo: formattedDateTo,
			roomsAndGuests,
			value: smartyDisplay,
		})

		const backTabRedirectCriteria =
			hasBackTabRedirect && isMainPage && isCompareChecked

		const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')
		const localDestcode = destCode?.includes(':')
			? destCode.substring(destCode.indexOf(':') + 1)
			: destCode

		const getCommonFormData = () => ({
			...selectedItem,
			dateFrom,
			dateTo,
			roomsAndGuests,
			backTabPartner,
			frontTabPartner,
			buid,
			...(hasCityDestcode && { ptid: localDestcode }),
		})

		const handleRedirect = (formType: FORM_TYPE) => {
			const formData = getFormData({
				...getCommonFormData(),
				formType,
			})

			if (formType === FORM_TYPE.BACK) {
				window.open(url, '_blank', 'noopener,noreferrer')
				submitHiddenForm(formData, redirectFormRef.current as HTMLFormElement)
			} else if (formType === FORM_TYPE.FRONT) {
				const baseUrl = window.location.origin
				const passthruUrl = `${baseUrl}/${urlParams.lang}/passthru?${serialize(
					formData
				)}`
				window.open(passthruUrl, '_blank', 'noopener,noreferrer')
				router.push(url)
				setFrontFormData(formData)
			}
		}

		if (backTabRedirectCriteria) {
			handleRedirect(FORM_TYPE.BACK)
		} else if (frontTabRedirectCriteria) {
			handleRedirect(FORM_TYPE.FRONT)
		} else {
			router.push(url)
		}
	}

	const handleUserEmailChange = (value: string) => {
		setUserEmail(value)
		setEmailError('')
	}
	const handleLocationError = () => {
		onInputFocus()
		if (!isMobile) {
			setTimeout(() => {
				setInputError(t('locationError'))
			}, 100)
		}

		emitSearchWithoutLocationGAEvent()
	}

	const handleSignupAndSearch = (email: string) => {
		if (!selectedItem) {
			handleLocationError()
			return
		}

		if (userEmail.length) {
			const validEmail = validateEmail(userEmail)

			if (!validEmail) {
				setEmailError(t('emailError'))
				return
			}
		}

		if (!userEmail && !email) {
			emitFormEmailSubscribeBounceGAEvent()
			setIsOpenEmailSignup(false)
			handleSearch()
			return
		}

		const buid = searchParams.get('buid') || null
		const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')

		const formData = getFormData({
			...selectedItem,
			dateFrom,
			dateTo,
			roomsAndGuests,
			buid,
			...(hasCityDestcode && { ptid: destCode }),
		})

		const {
			_adults,
			_children,
			'destination-id': _,
			...restFormData
		} = formData
		const { placeID, kayakId: hotelID, kayakType } = selectedItem
		const isHotel = selectedItem.loctype === SearchItemLocationType.HOTEL
		const currency =
			searchParams.get('currency') ||
			userData?.selectedCurrency?.abbreviation ||
			userData.defaultCurrency.abbreviation

		priceAlertSubscription({
			...restFormData,
			email: userEmail || email,
			adults: formData._adults,
			children: formData._children,
			lng: urlParams.lang as string,
			currency: currency,
			'destination-id': placeID && placeID,
		})
			.then(() => {
				console.log('subscription successful')
				emitFormEmailSubmitGAEvent()
			})
			.catch((error) => {
				console.error('An error occurred during subscription:', error)
			})
			.finally(() => {
				setUserData({ ...userData, email: userEmail || email })
				handleSearch()
			})
	}

	const handleOpenEmailDialog = () => {
		if (!selectedItem) {
			if (isMobile) {
			} else {
				onInputFocus()
				setInputError(t('locationError'))
			}

			return
		}
		setIsOpenEmailSignup(true)
	}

	const onCalendarClose = () => {
		if (dateFrom && dateTo === null) {
			const suggestedCheckout = addDays(dateFrom, 1)
			const maxDate = addMonths(new Date(), 12)

			if (suggestedCheckout <= maxDate) {
				setDateTo(suggestedCheckout)
			}
		}

		setIsDatepickerOpen(false)
		handleClose()
	}

	const onRoomsAndGuestsOpen = () => {
		handleOpenInput(INPUT.GUESTS_SELECT)
		emitGuestsClickGAEvent()
	}

	const onRoomsAndGuestsClose = () => {
		if (!isDesktop) {
			smoothScrollTo(0, 0)
		}

		handleClose()

		emitGuestsSelectGAEvent({
			rooms: roomsAndGuests.length,
			adults: roomsAndGuests.reduce((acc, room) => acc + room.adults, 0),
			children: roomsAndGuests.reduce(
				(acc, room) => acc + room.childAge.length,
				0
			),
			total: getGuestsCount(roomsAndGuests),
		})
	}

	const handleApplyGuestsAndSearch = () => {
		handleOpenInput(null)
		handleSearch()
	}

	useEffect(() => {
		if (isMobile && isDatepickerOpen) {
			setTimeout(() => scrollToSelectedMonth(dateFrom as Date), 0)
		}
	}, [isDatepickerOpen])

	const handleApplyDates = () => {
		setIsDatepickerOpen(false)
		setPreviousDateFrom(dateFrom)
		setPreviousDateTo(dateTo)
		eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.MAIN_FORM)
	}

	const handleOpenInput = (input: AvailableInputTypes) => {
		if (openedInput === INPUT.LOCATION && input === null) {
			smoothScrollTo(0, 0)
		}

		setInputError(null)
		setOpenedInput(input)

		if (smHeight && isDesktop) {
			bookingRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		}
	}

	const handleClose = () => {
		setOpenedInput(null)
		if (!isDesktop) {
			eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.MAIN_FORM)
		}
		document.body.style.overflowY = 'initial'
	}

	const onInputFocus = (input: INPUT = INPUT.LOCATION) => {
		const isLocationInput = input === INPUT.LOCATION
		handleOpenInput(input)

		if (isLocationInput) {
			bookingInputDestRef.current?.focus()
		}

		if (!isDesktop) {
			document.body.style.overflowY = 'hidden'
		}
	}

	const handlePriceAlertClick = () => {
		setIsAlertChecked(!alertChecked)
		if (!alertChecked) {
			// opposite of checked, because it reads prev state
			emitSignUpDealsGAEvent()
		}
	}

	return (
		<div
			className={classNames('booking__form', {
				'pt-6 max-lg:border border-text-300 md:max-lg:mb-5 md:max-lg:mx-5':
					isMainPage,
			})}
		>
			{isMainPage && (
				<div className="text-center">
					<SiteHeading
						showNewHeading={showNewHeading}
						customHeading={customHeading}
						selectedItem={selectedItem}
						isDesktop={isDesktop}
						sanityData={sanityData}
						isMd={isMd}
					/>
				</div>
			)}
			{/* Anchor (autoscroll) */}
			<div className="relative" style={{ top: '-110px' }} ref={bookingRef} />
			<div
				ref={bookingInputsRef}
				className={classNames(
					'booking w-full flex lg:mt-8 justify-center flex-wrap mx-auto',
					{ 'max-lg:px-3 xl:max-w-[1048px] lg:max-xl:container': isMainPage },
					{ 'max-w-[1048px]': !isMainPage }
				)}
			>
				<form
					ref={redirectFormRef}
					id="redirect-form"
					method="post"
					action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
				/>
				<form
					ref={redirectFormBlankRef}
					id="redirect-form_blank"
					method="post"
					target="_blank"
					action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
				/>
				<div className="booking__wrapper">
					<div
						className={classNames(
							'flex-1 w-full lg:h-full md:max-lg:mb-2 max-lg:mb-3',
							{
								'lg:my-0': isMainPage,
							}
						)}
					>
						<SearchAutosuggest
							ref={bookingInputDestRef}
							defaultValue={capitalize(
								searchValue?.displayValue || searchValue?.displayName || ''
							)}
							suggestions={suggestions}
							inputError={inputError}
							onSuggestionsFetchRequested={onSuggestionsFetchRequested}
							onSuggestionSelected={onSuggestionSelected}
							onInputChange={onInputChange}
							onInputFocus={onInputFocus}
							onInputBlur={handleClose}
							inputClassName={classNames('search-autosuggest__input', {
								'search-autosuggest__input--is-open new-autosuggest-input--open':
									openedInput === INPUT.LOCATION,
							})}
							lang={urlParams.lang as string}
							pageType={
								isMainPage
									? PAGE_TYPE.MAIN_PAGE
									: isHotelPage
									? PAGE_TYPE.HOTEL_PAGE
									: PAGE_TYPE.RESULTS_PAGE
							}
						/>
					</div>
					<BookingDatepicker
						dateFrom={dateFrom}
						dateTo={dateTo}
						isDatepickerOpen={isDatepickerOpen}
						openedInput={openedInput}
						isMobile={!isDesktop}
						isDesktop={isDesktop}
						locale={locale}
						onDateChange={onDateChange}
						onCalendarClose={onCalendarClose}
						openCalendar={openCalendar}
						handleCancelDateSelection={handleCancelDateSelection}
						handleApplyDates={handleApplyDates}
					/>

					<div ref={roomsInputRef} className="relative -top-2"></div>
					<div
						className={classNames(
							'booking__guests-input booking__additional-input',
							{
								'guests-open !border-text-700':
									openedInput === INPUT.GUESTS_SELECT,
							}
						)}
						id={INPUT.GUESTS_SELECT}
						data-test-id="GUESTS_SELECT"
					>
						<FormInput
							id={'rooms-and-guests-input'}
							onClick={onRoomsAndGuestsOpen}
							value={roomsAndGuestsText}
							className={classNames('outline-none max-lg:h-14 rounded-full', {
								'rooms-and-guests-input--is-open':
									openedInput === INPUT.GUESTS_SELECT,
							})}
							readonly
							ref={bookingInputGuestsRef}
						>
							<Icons.iconGuests />
						</FormInput>
						{!isDesktop ? (
							<RemoveScroll
								enabled={isMobile && openedInput === INPUT.GUESTS_SELECT}
								removeScrollBar={false}
							>
								<FullScreenDrawer
									isOpen={openedInput === INPUT.GUESTS_SELECT}
									onClose={onRoomsAndGuestsClose}
									headerText={t('roomsAndGuestsLabel')}
								>
									<div className="flex flex-col h-full">
										<GuestsSelect
											onChange={handleRoomsAndGuestsChange}
											initialValue={roomsAndGuests}
											onClose={onRoomsAndGuestsClose}
										/>
										<div className="mt-4 sticky bottom-8 px-4">
											<Button
												id="search-button"
												big
												bgColor="main"
												textColor="white"
												onClick={handleApplyGuestsAndSearch}
												name="Search"
												className="font-bold lg:hover:opacity-80 mx-auto text-xl max-w-screen-md !bg-gradient-to-r h-14 from-main to-[#E1004B] !rounded-full transition-all ease-linear flex items-center justify-center w-full"
											>
												<Icons.iconLens className="mr-3" />
												{t('newDesign.searchButton')}
											</Button>
										</div>
									</div>
								</FullScreenDrawer>
							</RemoveScroll>
						) : (
							<Popup
								className="rounded-lg right-0 top-0 lg:top-[68px] z-[70]"
								isOpen={openedInput === INPUT.GUESTS_SELECT}
								onClose={onRoomsAndGuestsClose}
							>
								<div className="flex flex-col h-full">
									<GuestsSelect
										onChange={handleRoomsAndGuestsChange}
										initialValue={roomsAndGuests}
										onClose={onRoomsAndGuestsClose}
									/>
								</div>
							</Popup>
						)}
					</div>
					<div
						data-test="search-button"
						className="hidden lg:block lg:order-2 xl:order-none"
					>
						<Button
							id="search-button"
							bold
							textColor="white"
							onClick={alertChecked ? handleOpenEmailDialog : handleSearch}
							name="Search"
							className={classNames(
								'!bg-gradient-to-r text-xl h-14 hover:opacity-80 from-main to-[#E1004B] !rounded-full transition-all ease-linear w-fit flex items-center justify-center'
							)}
						>
							<Icons.iconLens className="xl:mr-3" />
							<span className="hidden xl:inline">
								{t('newDesign.searchButton')}
							</span>
						</Button>
					</div>
				</div>

				{!isDesktop && isMainPage && (
					<>
						<div className="lg:hidden justify-between w-full lg:px-2 mt-6">
							{hasBackTabRedirect && isMainPage && (
								<Checkbox
									labelLeft={isPartnerBooking ? t('compareWith') : ''}
									labelRight={
										isPartnerBooking ? `Booking.com` : t('compareWithOthers')
									}
									checked={isCompareChecked}
									onChange={setIsCompareChecked}
									testId="compare-with-booking"
									variant="light"
								/>
							)}
						</div>
						<div className="price-alert-switch-mobile mb-6 mt-3 md:mt-0 w-full flex justify-start">
							<Checkbox
								labelRight={t('priceAlertSwitch')}
								checked={alertChecked}
								onChange={handlePriceAlertClick}
								testId="price-alert-switch"
								variant="main-dark"
							/>
						</div>
						{alertChecked && (
							// tablet & mobile input
							<EmailSignup
								destination={selectedItem as SearchItem}
								startDate={dateFrom as Date}
								endDate={dateTo as Date}
								isOpen={true}
								onChange={handleUserEmailChange}
								emailInputError={emailError}
								onSubmit={() => console.log('Submit')}
							/>
						)}
					</>
				)}
				<div
					data-test="search-button"
					className="block lg:hidden w-full lg:px-2 lg:order-3 xl:order-none"
				>
					<Button
						id="search-button"
						big
						bgColor="main"
						textColor="white"
						onClick={
							alertChecked
								? () => handleSignupAndSearch(userEmail)
								: handleSearch
						}
						name="Search"
						className={classNames(
							'font-bold text-xl !bg-gradient-to-r h-14 from-main to-[#E1004B] !rounded-full transition-all ease-linear flex items-center justify-center',
							{
								'mt-3': !isMainPage,
							}
						)}
					>
						<Icons.iconLens className="mr-3" />

						{alertChecked
							? t('signupAndSearch')
							: isMobile && isMainPage
							? t('newDesign.searchButtonMobile')
							: t('newDesign.searchButton')}
					</Button>
				</div>
				<div
					className={classNames(
						'hidden lg:order-2 xl:order-none w-full xl:max-w-[1040px]',
						{
							hidden: !isMainPage,
							'lg:flex ': isMainPage,
						}
					)}
				>
					<div
						className={classNames(
							' flex items-center justify-between mt-5 w-full ps-2 lg:ps-5 pe-2 lg:pe-5 mb-4 lg:mb-0',
							{
								'lg:!justify-end': !hasBackTabRedirect && isMainPage,
							}
						)}
					>
						{hasBackTabRedirect && isMainPage && (
							<div className="compare-checkbox-desktop">
								<Checkbox
									labelLeft={isPartnerBooking ? t('compareWith') : ''}
									labelRight={
										isPartnerBooking ? `Booking.com` : t('compareWithOthers')
									}
									checked={isCompareChecked}
									onChange={setIsCompareChecked}
									testId="compare-with-booking"
									variant="white"
								/>
							</div>
						)}
						{isDesktop && isMainPage && (
							<div className="price-alert-switch">
								<Checkbox
									labelRight={t('priceAlertSwitch')}
									checked={alertChecked}
									onChange={handlePriceAlertClick}
									testId="price-alert-switch"
									variant="white"
								/>
							</div>
						)}
					</div>
				</div>
			</div>
			{isOpenEmailSignup && selectedItem && isDesktop && (
				<EmailSignup
					destination={selectedItem as SearchItem}
					startDate={dateFrom as Date}
					endDate={dateTo as Date}
					isOpen={true}
					onSubmit={handleSignupAndSearch}
					guestCount={getGuestsCount(roomsAndGuests)}
				/>
			)}
		</div>
	)
}

export default dynamic(() => Promise.resolve(Booking), {
	ssr: false,
	suspense: true,
	loading: () => (
		<>
			<div className="home-page-placeholder">
				<BookingPlaceholder />
			</div>
			<div className="results-page-placeholder">{/* <TopLoading /> */}</div>
		</>
	),
})
